import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import FollowingModal from './FollowingModal.tsx';
import { MessagingIcon } from './messaging/MessagingIcon.tsx';
import { MessagingIconSimple } from './messaging/MessagingIconSimple.tsx';

interface UserProfileProps {
  userId: string;
  onClose?: () => void;
  onViewUser?: (userId: string) => void;
}

interface UserData {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  bio?: string;
  profile_picture?: string;
  user_type: string;
  date_joined: string;
}

interface UserStats {
  followers: number;
  following: number;
  posts: number;
}

export const UserProfile: React.FC<UserProfileProps> = ({ userId, onClose, onViewUser }) => {
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState<UserData | null>(null);
  const [stats, setStats] = useState<UserStats>({ followers: 0, following: 0, posts: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [showFollowing, setShowFollowing] = useState<'following' | 'followers' | null>(null);
   const [selectedPost, setSelectedPost] = useState<any | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  

  const API_BASE_URL = 'https://aztrainersbackend.onrender.com';

  useEffect(() => {
    if (userId) {
      fetchUserProfile();
    }
  }, [userId]);

  

  const fetchUserProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);

        // Fetch real stats
        await fetchStats();

        // Check actual follow status if we have a current user
        if (currentUser && currentUser.id !== userId) {
          await checkFollowStatus();
        }
      } else {
        setError('Failed to load user profile');
      }
    } catch (err) {
      setError('Error loading user profile');
      console.error('Error fetching user profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/stats/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats({
          followers: data.followers_count || 0,
          following: data.following_count || 0,
          posts: data.posts_count || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set default values if fetch fails
      setStats({ followers: 0, following: 0, posts: 0 });
    }
  };

  // Add new function to check follow status
  const checkFollowStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('Checking follow status for user:', userId);

      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/is_following/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      console.log('Follow status response:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Follow status data:', data);
        setIsFollowing(data.is_following || false);
      } else {
        console.log('Follow status endpoint failed, trying alternative approach');
        // Try alternative: check if user is in current user's following list
        await checkFollowStatusAlternative();
      }
    } catch (error) {
      console.error('Error checking follow status:', error);
      // Try alternative approach
      await checkFollowStatusAlternative();
    }
  };

  // Alternative method: check current user's following list
  const checkFollowStatusAlternative = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('Trying alternative follow status check');

      const response = await fetch(`${API_BASE_URL}/api/users/${currentUser?.id}/following/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Following list data:', data);

        // Check if the current user being viewed is in the following list
        const isUserInFollowingList = data.users?.some((followedUser: any) => followedUser.id === userId);
        console.log('Is user in following list?', isUserInFollowingList);
        setIsFollowing(isUserInFollowingList || false);
      } else {
        console.log('Alternative method also failed, defaulting to false');
        setIsFollowing(false);
      }
    } catch (error) {
      console.error('Error with alternative follow status check:', error);
      setIsFollowing(false);
    }
  };

  const handleFollow = async () => {
    if (!currentUser || followLoading) return;

    setFollowLoading(true);
    try {
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_BASE_URL}/api/users/${userId}/follow/`, {
        method: isFollowing ? 'DELETE' : 'POST', // Use same endpoint, different methods
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        setIsFollowing(!isFollowing);
        // Update follower count
        setStats(prev => ({
          ...prev,
          followers: isFollowing ? prev.followers - 1 : prev.followers + 1
        }));
      } else {
        console.error('Failed to toggle follow status');
      }
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  const getFullName = (user: UserData) => {
    return `${user.first_name} ${user.last_name}`.trim() || user.username;
  };

  const isTrainer = user?.user_type === 'trainer';
  const isAdmin = user?.user_type === 'admin';

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-white/40 shadow-lg overflow-hidden">
            <div className="h-48 bg-gray-300"></div>
            <div className="p-6">
              <div className="flex items-end justify-between -mt-16 mb-4">
                <div className="flex items-end space-x-4">
                  <div className="w-32 h-32 bg-gray-300 rounded-full"></div>
                  <div className="pb-2">
                    <div className="h-8 bg-gray-300 rounded w-48 mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded w-32"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-white/40 shadow-lg p-8 text-center">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
          <p className="text-gray-600 mb-4">{error || 'User not found'}</p>
          {onClose && (
            <button
              onClick={onClose}
              className="bg-[#E6A756] text-[#5D4037] px-4 py-2 rounded-full hover:bg-[#D2824C] transition-colors font-medium"
            >
              Go Back
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Profile Header - Same styling as your Profile.tsx */}
      <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-white/40 shadow-lg overflow-hidden mb-6">
        <div className="px-6 pt-6 pb-6 relative">

          {/* Top Right Controls - Updated */}
          <div className="absolute top-4 right-4 flex items-center space-x-2">
            {/* Add Messaging Icon for other users only */}
            {currentUser && currentUser.id !== userId && (
              <MessagingIconSimple
                targetUser={{
                  id: user.id,
                  username: user.username,
                  first_name: user.first_name,
                  last_name: user.last_name,
                  profile_picture: user.profile_picture
                }}
                className="bg-white/20 backdrop-blur-sm border border-gray-200"
              />
            )}

            {/* Back Button */}
            {onClose && (
              <button
                onClick={onClose}
                className="bg-white/20 backdrop-blur-sm text-gray-700 px-3 py-1 rounded-full text-sm hover:bg-white/30 transition-colors border border-gray-200"
              >
                ← Back
              </button>
            )}
          </div>

          {/* Profile Picture and Basic Info */}
          <div className="flex items-end justify-between mb-4">
            <div className="flex items-end space-x-4">
              <div className="w-32 h-32 bg-gray-300 rounded-full border-4 border-white shadow-lg overflow-hidden">
                {user.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={getFullName(user)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#E6A756] to-[#D2824C] flex items-center justify-center text-white font-bold text-2xl">
                    {user.first_name?.[0] || user.username[0]}
                    {user.last_name?.[0] || ''}
                  </div>
                )}
              </div>
              <div className="pb-2">
                <h1 className="text-2xl font-semibold text-gray-800">{getFullName(user)}</h1>
                <p className="text-gray-600">@{user.username}</p>
                {isTrainer && (
                  <span className="inline-block bg-[#9CAF88] text-[#FEFCF8] text-sm px-3 py-1 rounded-full mt-2 font-medium">
                    Certified Trainer
                  </span>
                )}
                {isAdmin && (
                  <span className="inline-block bg-[#8B5A3C] text-[#FEFCF8] text-sm px-3 py-1 rounded-full mt-2 font-medium">
                    Administrator
                  </span>
                )}
              </div>
            </div>

            {/* Follow/Unfollow Button */}
            <div className="flex space-x-2">
              {currentUser && currentUser.id !== userId && (
                <button
                  onClick={handleFollow}
                  disabled={followLoading}
                  className={`px-6 py-2 rounded-full transition-colors font-medium disabled:opacity-50 ${isFollowing
                    ? 'bg-[#F8F4EE] text-[#5D4037] hover:bg-[#F5EFE7] border border-[#D4C4B0]'
                    : 'bg-[#9CAF88] text-[#FEFCF8] hover:bg-[#7A8471]'
                    }`}
                >
                  {followLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-[#5D4037] border-t-transparent rounded-full animate-spin"></div>
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <>
                      {isFollowing ? 'Following' : 'Follow'}
                    </>
                  )}
                </button>
              )}


            </div>
          </div>

          {/* User Role Info */}
          <div className="mb-4 p-3 bg-white/40 rounded-lg">
            <h3 className="text-gray-800 font-semibold">Account Type</h3>
            <p className="text-gray-700 capitalize">{user.user_type}</p>
            <p className="text-gray-600 text-sm">
              Member since {new Date(user.date_joined).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>

          {/* Bio */}
          <div className="mb-4">
            <h3 className="text-gray-800 mb-2">Bio</h3>
            <p className="text-gray-700">
              {user.bio || (
                isAdmin ? 'Platform administrator with full access to manage trainers and clients.' :
                  isTrainer ? 'Certified fitness trainer helping clients reach their goals.' :
                    'Fitness enthusiast on a journey to better health.'
              )}
            </p>
          </div>

          {/* Stats */}
          <div className="flex space-x-8 mb-6">
            <div className="text-center">
              <div className="text-xl font-semibold text-gray-800">{stats.posts}</div>
              <div className="text-gray-600">Posts</div>
            </div>
            <button
              onClick={() => setShowFollowing('followers')}
              className="text-center hover:bg-white/20 rounded p-2 transition-colors"
            >
              <div className="text-xl font-semibold text-gray-800">{stats.followers}</div>
              <div className="text-gray-600">Followers</div>
            </button>
            <button
              onClick={() => setShowFollowing('following')}
              className="text-center hover:bg-white/20 rounded p-2 transition-colors"
            >
              <div className="text-xl font-semibold text-gray-800">{stats.following}</div>
              <div className="text-gray-600">Following</div>
            </button>
          </div>
        </div>
      </div>

      {/* Following/Followers Modal */}
      <FollowingModal
        isOpen={showFollowing !== null}
        onClose={() => setShowFollowing(null)}
        onViewUser={(viewUserId) => {
          if (onViewUser) {
            onViewUser(viewUserId);
          }
          setShowFollowing(null); // Close modal after navigation
        }}
        type={showFollowing || 'following'}
        userId={userId} // Pass the profile user's ID to show their followers/following
      />

      {/* Posts Grid - Instagram Style */}
      <div className="bg-white/60 backdrop-blur-sm rounded-lg border border-white/40 shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Posts</h3>
        {/* Pass userId to ProfilePostsGrid to show this user's posts */}
        <div className="mt-8">
          <UserPostsGrid userId={userId} />
        </div>
      </div>
    </div>
  );
};

// Separate component for user posts grid (similar to ProfilePostsGrid but for other users)
const UserPostsGrid: React.FC<{ userId: string }> = ({ userId }) => {
  
  const { user } = useAuth();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState<any | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

   const API_BASE_URL = 'https://aztrainersbackend.onrender.com';

  useEffect(() => {
    fetchUserPosts();
  }, [userId]);

  const fetchUserPosts = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`https://aztrainersbackend.onrender.com/api/users/${userId}/posts/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Filter posts that have media
        const postsWithMedia = data.posts.filter((post: any) =>
          post.media_url || post.image
        );
        setPosts(postsWithMedia);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Failed to fetch user posts:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  };


  const openPost = async (post: any): Promise<void> => {
    setSelectedPost(post);
    setCommentsLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/posts/${post.id}/comments/`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setComments(data.comments || []);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const closePost = (): void => {
    setSelectedPost(null);
    setComments([]);
    setNewComment('');
  };

  const handleLike = async (postId: string): Promise<void> => {
    const post = posts.find(p => p.id === postId);
    if (!post) return;

    try {
      const token = localStorage.getItem('authToken');
      
      if (post.liked_by_user) {
        await fetch(`${API_BASE_URL}/api/posts/${postId}/unlike/`, {
          method: 'DELETE',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
      } else {
        await fetch(`${API_BASE_URL}/api/posts/${postId}/like/`, {
          method: 'POST',
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          },
        });
      }
      
      const updatedPosts = posts.map(p => 
        p.id === postId 
          ? { 
              ...p, 
              likes_count: post.liked_by_user ? p.likes_count - 1 : p.likes_count + 1, 
              liked_by_user: !post.liked_by_user 
            }
          : p
      );
      setPosts(updatedPosts);
      
      if (selectedPost && selectedPost.id === postId) {
        setSelectedPost(updatedPosts.find(p => p.id === postId) || null);
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
    }
  };

  const handleSubmitComment = async (): Promise<void> => {
    if (!newComment.trim() || submittingComment || !selectedPost) return;

    setSubmittingComment(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/api/posts/${selectedPost.id}/comments/`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: newComment.trim() }),
      });
      
      if (response.ok) {
        const data = await response.json();
        const newCommentObj = {
          id: data.comment_id || Date.now().toString(),
          content: newComment.trim(),
          created_at: new Date().toISOString(),
          author: {
            id: user!.id,
            username: user!.username,
            first_name: user!.first_name,
            last_name: user!.last_name || '',
            profile_picture: user!.profile_picture,
            user_type: user!.role
          }
        };
        
        setComments([...comments, newCommentObj]);
        setNewComment('');
        
        setPosts(posts.map(p => 
          p.id === selectedPost.id 
            ? { ...p, comments_count: p.comments_count + 1 }
            : p
        ));
        
        setSelectedPost({
          ...selectedPost,
          comments_count: selectedPost.comments_count + 1
        });
      }
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'now';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return `${Math.floor(diffInMinutes / 1440)}d`;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-1">
        {[...Array(9)].map((_, i) => (
          <div key={i} className="aspect-square bg-gray-200 animate-pulse rounded-sm"></div>
        ))}
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 mb-2">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">No posts yet</h3>
        <p className="text-gray-500">This user hasn't shared any photos or videos.</p>
      </div>
    );
  }

  return (
    <>
    <div className="grid grid-cols-3 gap-1">
      {posts.map((post) => (
        <div
          key={post.id}
          onClick={() => openPost(post)}
          className="relative aspect-square cursor-pointer group overflow-hidden rounded-sm"
        >
          {post.media_type === 'video' ? (
            <div className="relative">
              <img
                src={post.thumbnail_url || post.media_url?.replace('.mp4', '.jpg')}
                alt="Post content"
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
              <div className="absolute top-2 right-2">
                <svg className="h-4 w-4 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          ) : (
            <img
              src={post.media_url || post.image}
              alt="Post content"
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
            />
          )}

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-25 transition-all duration-200 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-6 text-white">
              <div className="flex items-center space-x-1">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                </svg>
                <span className="font-semibold">{post.likes_count}</span>
              </div>
              <div className="flex items-center space-x-1">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="font-semibold">{post.comments_count}</span>
              </div>
            </div>
          </div>
          
        </div>
      ))}
    </div>
    {selectedPost && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex">
            {/* Media Section */}
            <div className="flex-1 bg-black flex items-center justify-center">
              {selectedPost.media_type === 'video' ? (
                <video
                  src={selectedPost.media_url}
                  controls
                  className="max-w-full max-h-full"
                />
              ) : (
                <img
                  src={selectedPost.media_url || selectedPost.image}
                  alt="Post content"
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>

            {/* Content Section */}
            <div className="w-96 flex flex-col">
              {/* Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-300 to-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {selectedPost.author.first_name[0]}{selectedPost.author.last_name[0]}
                  </div>
                  <div>
                    <span className="font-semibold text-sm">{selectedPost.author.first_name} {selectedPost.author.last_name}</span>
                    {selectedPost.author.user_type === 'trainer' && (
                      <span className="ml-2 bg-green-400 text-gray-800 text-xs px-2 py-0.5 rounded-full">
                        Trainer
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={closePost}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Post Content */}
              <div className="p-4 border-b">
                <p className="text-sm">{selectedPost.content}</p>
                <span className="text-xs text-gray-500 mt-2 block">
                  {formatTimeAgo(selectedPost.created_at)}
                </span>
              </div>

              {/* Comments */}
              <div className="flex-1 p-4 overflow-y-auto">
                {commentsLoading ? (
                  <div className="text-center text-gray-500">Loading comments...</div>
                ) : comments.length === 0 ? (
                  <div className="text-center text-gray-500">No comments yet</div>
                ) : (
                  <div className="space-y-3">
                    {comments.map((comment) => (
                      <div key={comment.id} className="flex space-x-2">
                        <div className="w-6 h-6 bg-gradient-to-br from-green-300 to-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {comment.author.first_name[0]}
                        </div>
                        <div className="flex-1 text-sm">
                          <span className="font-semibold">{comment.author.first_name}</span>
                          {' '}
                          <span>{comment.content}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-4 border-t">
                <div className="flex space-x-4 mb-2">
                  <button
                    onClick={() => handleLike(selectedPost.id)}
                    className={`flex items-center space-x-1 ${selectedPost.liked_by_user ? 'text-red-500' : 'text-gray-700 hover:text-red-500'
                      }`}
                  >
                    <svg className="h-6 w-6" fill={selectedPost.liked_by_user ? "currentColor" : "none"} viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                  <button className="text-gray-700 hover:text-gray-900">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </button>
                </div>
                <div className="text-sm font-semibold mb-3">{selectedPost.likes_count} likes</div>

                {/* Add Comment Section */}
                <div className="border-t pt-3">
                  <div className="flex space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-300 to-green-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                    <div className="flex-1">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Add a comment..."
                        className="w-full p-2 border border-gray-200 rounded-lg bg-white/80 resize-none focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent text-sm"
                        rows={2}
                        disabled={submittingComment}
                      />
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={handleSubmitComment}
                          disabled={!newComment.trim() || submittingComment}
                          className="bg-green-400 text-gray-800 px-3 py-1 rounded-full hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                          {submittingComment ? 'Posting...' : 'Post'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      </>
  );
};

export default UserProfile;